import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sun, Moon, Building2, Upload as UploadIcon, Image as ImageIcon, X, ShieldCheck, Bot, Loader2, CheckCircle2, KeyRound, Camera, Mail as MailIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { theme, toggle } = useTheme();
  const [settings, setSettings] = useState({ app_name: "ResumeForge", tagline: "", login_image_url: null, primary_color: "#1E5BFF", ai_provider: "openai", ai_model: "gpt-5.2", ai_custom_key: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);
  const [profile, setProfile] = useState({ full_name: user?.full_name || "", company: user?.company || "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);
  const avatarRef = useRef(null);
  const faviconRef = useRef(null);
  const brandLogoRef = useRef(null);

  const isAdmin = ["super_admin", "company_admin"].includes(user?.role);

  useEffect(() => { api.get("/settings/app").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => { setProfile({ full_name: user?.full_name || "", company: user?.company || "" }); }, [user]);

  const initials = (user?.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const r = await api.put("/auth/profile", profile);
      refreshUser(r.data);
      toast.success("Profile updated");
    } catch { toast.error("Failed to update"); } finally { setSavingProfile(false); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      refreshUser(r.data);
      toast.success("Avatar updated");
    } catch { toast.error("Avatar upload failed"); } finally { setUploadingAvatar(false); if (avatarRef.current) avatarRef.current.value = ""; }
  };

  const changePwd = async () => {
    if (pwd.new_password !== pwd.confirm) { toast.error("New passwords don't match"); return; }
    if (pwd.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Change failed"); } finally { setSavingPwd(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/app", settings);
      setSettings(r.data);
      toast.success("Workspace settings saved");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/settings/app/upload-login-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings(r.data);
      toast.success("Login image updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/app", { login_image_url: "" });
      setSettings({ ...r.data, login_image_url: null });
      toast.success("Login image removed");
    } catch { toast.error("Failed to remove"); } finally { setSaving(false); }
  };

  const onFavicon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/settings/app/upload-favicon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings(r.data);
      // Hot-update favicon in current tab too
      const url = r.data.favicon_url ? `${BACKEND_URL}${r.data.favicon_url}` : null;
      if (url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
        link.href = url;
      }
      toast.success("Favicon updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); if (faviconRef.current) faviconRef.current.value = ""; }
  };

  const onBrandLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/settings/app/upload-brand-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings(r.data);
      toast.success("Brand logo updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setUploading(false); if (brandLogoRef.current) brandLogoRef.current.value = ""; }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold">Workspace</div>
      <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Settings</h1>
      <p className="text-muted-foreground text-sm mt-1">Profile, branding, appearance — all in one place.</p>

      <div className="grid grid-cols-1 gap-4 mt-8">
        {/* Profile */}
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="display font-bold mb-4 flex items-center gap-2"><ShieldCheck className="size-4" /> Profile</div>
            <div className="flex items-start gap-4 mb-5">
              <div className="relative">
                <Avatar className="size-20 border-2 border-border">
                  <AvatarImage src={user?.avatar_url ? `${BACKEND_URL}${user.avatar_url}` : undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl">{initials}</AvatarFallback>
                </Avatar>
                <input ref={avatarRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={onAvatar} className="hidden" data-testid="avatar-input" />
                <button onClick={() => avatarRef.current?.click()} disabled={uploadingAvatar} className="absolute -bottom-1 -right-1 size-7 grid place-items-center rounded-full bg-primary text-primary-foreground border-2 border-card lift" data-testid="avatar-upload-button" title="Change avatar">
                  {uploadingAvatar ? <Loader2 className="size-3.5 animate-spin" /> : <Camera className="size-3.5" />}
                </button>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Full name"><Input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} data-testid="profile-name-input" /></Field>
                <Field label="Email"><Input value={user?.email || ""} readOnly /></Field>
                <Field label="Company"><Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} data-testid="profile-company-input" /></Field>
                <Field label="Role"><Input value={(user?.role || "").replace("_", " ")} readOnly /></Field>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveProfile} disabled={savingProfile} className="lift" data-testid="profile-save-button">{savingProfile ? "Saving…" : "Save profile"}</Button>
            </div>
          </CardContent>
        </Card>

        {/* Change password */}
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="display font-bold mb-4 flex items-center gap-2"><KeyRound className="size-4" /> Change password</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Current password"><Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="pwd-current-input" /></Field>
              <Field label="New password"><Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="pwd-new-input" /></Field>
              <Field label="Confirm new password"><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="pwd-confirm-input" /></Field>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={changePwd} disabled={savingPwd || !pwd.current_password || !pwd.new_password} className="lift" data-testid="pwd-save-button">{savingPwd ? "Updating…" : "Update password"}</Button>
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="display font-bold mb-4">Appearance</div>
            <div className="flex items-center justify-between border border-border rounded-md p-3">
              <div className="flex items-center gap-3">
                {theme === "dark" ? <Moon className="size-4" /> : <Sun className="size-4" />}
                <div>
                  <div className="font-semibold text-sm">Dark mode</div>
                  <div className="text-xs text-muted-foreground">Toggle between light and dark themes.</div>
                </div>
              </div>
              <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-darkmode-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Admin branding */}
        {isAdmin ? (
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="display font-bold mb-1 flex items-center gap-2"><Building2 className="size-4" /> Workspace branding</div>
              <div className="text-xs text-muted-foreground mb-4">Visible on the login page and across the app.</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="App name">
                  <Input value={settings.app_name || ""} onChange={(e) => setSettings({ ...settings, app_name: e.target.value })} data-testid="settings-appname-input" />
                </Field>
                <Field label="Primary color">
                  <Input type="color" value={settings.primary_color || "#1E5BFF"} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })} data-testid="settings-primary-input" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Tagline (login page)">
                    <Input value={settings.tagline || ""} onChange={(e) => setSettings({ ...settings, tagline: e.target.value })} data-testid="settings-tagline-input" />
                  </Field>
                </div>
              </div>

              <Separator className="my-5" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-xs font-semibold">Site favicon</Label>
                  <div className="text-xs text-muted-foreground mt-0.5 mb-3">Browser tab icon. PNG / SVG / ICO recommended (32×32 or 64×64).</div>
                  <div className="flex items-center gap-3">
                    <div className="size-14 rounded-md border border-border bg-muted grid place-items-center overflow-hidden">
                      {settings.favicon_url
                        ? <img src={`${BACKEND_URL}${settings.favicon_url}`} alt="favicon" className="w-full h-full object-contain" />
                        : <ImageIcon className="size-5 text-muted-foreground" />}
                    </div>
                    <input ref={faviconRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.ico" onChange={onFavicon} className="hidden" data-testid="settings-favicon-input" />
                    <Button size="sm" variant="outline" onClick={() => faviconRef.current?.click()} disabled={uploading} className="gap-2 lift" data-testid="settings-favicon-upload">
                      <UploadIcon className="size-4" /> {settings.favicon_url ? "Replace" : "Upload"}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Brand logo (for hero / emails)</Label>
                  <div className="text-xs text-muted-foreground mt-0.5 mb-3">Used in resume hero banners and outgoing emails. PNG / SVG with transparent background works best.</div>
                  <div className="flex items-center gap-3">
                    <div className="h-14 min-w-[80px] rounded-md border border-border bg-muted grid place-items-center overflow-hidden px-2">
                      {settings.brand_logo_url
                        ? <img src={`${BACKEND_URL}${settings.brand_logo_url}`} alt="brand" className="max-h-full max-w-full object-contain" />
                        : <ImageIcon className="size-5 text-muted-foreground" />}
                    </div>
                    <input ref={brandLogoRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={onBrandLogo} className="hidden" data-testid="settings-brand-logo-input" />
                    <Button size="sm" variant="outline" onClick={() => brandLogoRef.current?.click()} disabled={uploading} className="gap-2 lift" data-testid="settings-brand-logo-upload">
                      <UploadIcon className="size-4" /> {settings.brand_logo_url ? "Replace" : "Upload"}
                    </Button>
                  </div>
                </div>
              </div>

              <Separator className="my-5" />

              <div>
                <Label className="text-xs font-semibold">Login background image</Label>
                <div className="text-xs text-muted-foreground mt-0.5 mb-3">Upload a hero image (PNG / JPG / WEBP). Replaces the default backdrop on the login screen.</div>
                <div className="flex flex-col md:flex-row gap-4 items-start">
                  <div className="w-full md:w-72 aspect-[16/10] rounded-md border border-border overflow-hidden bg-muted grid place-items-center">
                    {settings.login_image_url ? (
                      <img src={`${BACKEND_URL}${settings.login_image_url}`} alt="login bg" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-xs text-muted-foreground flex flex-col items-center gap-2"><ImageIcon className="size-6" /> No image set</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={onUpload} className="hidden" data-testid="settings-login-image-input" />
                    <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2 lift" data-testid="settings-upload-button">
                      <UploadIcon className="size-4" /> {uploading ? "Uploading…" : settings.login_image_url ? "Replace image" : "Upload image"}
                    </Button>
                    {settings.login_image_url && (
                      <Button variant="outline" onClick={removeImage} className="gap-2 lift text-destructive" data-testid="settings-remove-image-button">
                        <X className="size-4" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={save} disabled={saving} data-testid="settings-save-button" className="lift">{saving ? "Saving…" : "Save workspace settings"}</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border">
            <CardContent className="p-5 text-center text-sm text-muted-foreground">
              Workspace branding is editable by admins only.
            </CardContent>
          </Card>
        )}

        {/* AI Provider Integration (Admin only) */}
        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="display font-bold mb-1 flex items-center gap-2"><Bot className="size-4" /> AI Integration</div>
              <div className="text-xs text-muted-foreground mb-4">Choose which AI model powers parsing, rewriting, auto-fit, copilot chat, and from-scratch generation. Switch any time without code changes.</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Provider">
                  <Select value={settings.ai_provider || "openai"} onValueChange={(v) => {
                    const defaults = { openai: "gpt-5.2", anthropic: "claude-sonnet-4-5-20250929", gemini: "gemini-2.5-pro" };
                    setSettings({ ...settings, ai_provider: v, ai_model: defaults[v] });
                  }}>
                    <SelectTrigger data-testid="settings-ai-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI (ChatGPT)</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                      <SelectItem value="gemini">Google (Gemini)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Model">
                  <Select value={settings.ai_model} onValueChange={(v) => setSettings({ ...settings, ai_model: v })}>
                    <SelectTrigger data-testid="settings-ai-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {settings.ai_provider === "openai" && (<>
                        <SelectItem value="gpt-5.2">GPT-5.2 (recommended)</SelectItem>
                        <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                        <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                        <SelectItem value="gpt-4o-mini">GPT-4o mini (fast)</SelectItem>
                        <SelectItem value="o3">o3 (reasoning)</SelectItem>
                        <SelectItem value="o3-mini">o3-mini</SelectItem>
                      </>)}
                      {settings.ai_provider === "anthropic" && (<>
                        <SelectItem value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</SelectItem>
                        <SelectItem value="claude-opus-4-5">Claude Opus 4.5</SelectItem>
                        <SelectItem value="claude-haiku-4-5">Claude Haiku 4.5 (fast)</SelectItem>
                      </>)}
                      {settings.ai_provider === "gemini" && (<>
                        <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro</SelectItem>
                        <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                        <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash (fast)</SelectItem>
                      </>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Custom API key (optional — leave blank to use Emergent Universal Key)">
                    <Input type="password" value={settings.ai_custom_key || ""} onChange={(e) => setSettings({ ...settings, ai_custom_key: e.target.value })} placeholder="sk-... (only if you want to use your own key)" data-testid="settings-ai-key" />
                  </Field>
                </div>
              </div>

              <div className="flex justify-between items-center mt-5">
                <Button variant="outline" onClick={async () => {
                  setTestingAi(true); setAiTestResult(null);
                  try {
                    // Save first so the test uses the latest config
                    await api.put("/settings/app", settings);
                    const r = await api.post("/settings/app/test-ai");
                    setAiTestResult({ ok: true, ...r.data });
                    toast.success("AI provider responded ✓");
                  } catch (e) {
                    setAiTestResult({ ok: false, error: e?.response?.data?.detail || "AI test failed" });
                    toast.error("AI test failed");
                  } finally { setTestingAi(false); }
                }} disabled={testingAi} className="gap-2 lift" data-testid="settings-test-ai">
                  {testingAi ? <Loader2 className="size-4 animate-spin" /> : <Bot className="size-4" />} Test connection
                </Button>
                <Button onClick={save} disabled={saving} data-testid="settings-ai-save" className="lift">{saving ? "Saving…" : "Save AI config"}</Button>
              </div>

              {aiTestResult && (
                <div className={`mt-4 rounded-md border p-3 text-sm ${aiTestResult.ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-destructive/30 bg-destructive/5"}`}>
                  {aiTestResult.ok ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="size-4 mt-0.5 text-emerald-600" />
                      <div>
                        <div className="font-semibold">Connected to {aiTestResult.provider} · {aiTestResult.model}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">Reply: <span className="italic">{aiTestResult.reply}</span></div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-destructive">{aiTestResult.error}</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {isAdmin && <EmailSettingsCard />}
        {isAdmin && <LetterheadCard settings={settings} setSettings={setSettings} save={save} saving={saving} />}
        {isAdmin && <FeatureFlagsCard settings={settings} setSettings={setSettings} save={save} saving={saving} />}
      </div>
    </div>
  );
}

function LetterheadCard({ settings, setSettings, save, saving }) {
  const set = (k, v) => setSettings((x) => ({ ...x, [k]: v }));
  const fields = [
    { k: "company_name", l: "Company name", ph: "Acme Recruitment Pvt Ltd" },
    { k: "company_address", l: "Company address", ph: "221B Baker Street, London" },
    { k: "company_phone", l: "Phone", ph: "+91 98765 43210" },
    { k: "company_email", l: "Email", ph: "hr@acme.com" },
    { k: "company_website", l: "Website", ph: "acme.com" },
    { k: "default_signatory_name", l: "Default signatory name", ph: "Priya Sharma" },
    { k: "default_signatory_title", l: "Default signatory title", ph: "Head of HR" },
  ];
  const uploads = [
    { k: "company_logo_url", l: "Company logo", endpoint: "upload-company-logo", testid: "letterhead-logo" },
    { k: "letterhead_header_url", l: "Letterhead header banner", endpoint: "upload-letterhead-header", testid: "letterhead-header" },
    { k: "letterhead_footer_url", l: "Letterhead footer banner", endpoint: "upload-letterhead-footer", testid: "letterhead-footer" },
    { k: "default_signature_url", l: "Default signature", endpoint: "upload-default-signature", testid: "letterhead-signature" },
    { k: "default_seal_url", l: "Default seal / stamp", endpoint: "upload-default-seal", testid: "letterhead-seal" },
  ];
  const upload = async (endpoint, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/settings/app/${endpoint}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettings(r.data);
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
  };
  return (
    <Card className="border-border">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="display font-bold text-lg">Company letterhead</div>
            <p className="text-xs text-muted-foreground">Applied automatically to every HR document (offer letters, NDAs, agreements, etc.). Per-template fields can override these defaults.</p>
          </div>
          <Button onClick={save} disabled={saving} className="lift" data-testid="letterhead-save">{saving ? "Saving…" : "Save details"}</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {fields.map((f) => (
            <div key={f.k}>
              <Label className="text-xs font-semibold">{f.l}</Label>
              <Input placeholder={f.ph} value={settings[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} className="mt-1" data-testid={`letterhead-${f.k}`} />
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-3">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Images</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {uploads.map((u) => (
              <div key={u.k} className="border border-dashed border-border rounded-md p-3" data-testid={u.testid}>
                <div className="text-xs font-semibold mb-2">{u.l}</div>
                {settings[u.k] ? (
                  <img src={`${BACKEND_URL}${settings[u.k]}`} alt={u.l} className="max-h-16 max-w-full object-contain bg-white border border-border rounded mb-2" data-testid={`${u.testid}-preview`} />
                ) : (
                  <div className="text-[11px] text-muted-foreground mb-2">No image</div>
                )}
                <div className="flex items-center gap-2">
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={(e) => { if (e.target.files?.[0]) upload(u.endpoint, e.target.files[0]); }} className="text-xs" data-testid={`${u.testid}-input`} />
                  {settings[u.k] && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => set(u.k, "")}>Remove</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FeatureFlagsCard({ settings, setSettings, save, saving }) {
  const MODULES = [
    { k: "organizer", l: "Candidates (Organizer)", hint: "Kanban board + folders" },
    { k: "upload", l: "Upload", hint: "Upload resumes via PDF/DOCX/photo" },
    { k: "builder", l: "AI Builder", hint: "Conversational resume generator" },
    { k: "job_profiles", l: "Job Profiles", hint: "Score candidates against JDs" },
    { k: "clients", l: "Clients", hint: "Companies + per-client pipelines" },
    { k: "submissions", l: "Submissions", hint: "Track resumes sent to clients" },
    { k: "email_center", l: "Email Center", hint: "SMTP/Resend outbound email" },
    { k: "interviews", l: "Interviews", hint: "Schedule + feedback (Teams/Zoom)" },
    { k: "documents", l: "HR Documents", hint: "Letters / NDAs / letterhead (admin)" },
    { k: "reports", l: "Reports", hint: "Pipeline + analytics" },
    { k: "templates", l: "Templates", hint: "Template designer" },
  ];
  const flags = settings.feature_flags || {};
  const set = (k, v) => setSettings({ ...settings, feature_flags: { ...flags, [k]: v } });
  return (
    <Card className="border-border">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="display font-bold text-lg">Module visibility</div>
            <p className="text-xs text-muted-foreground">Toggle which modules appear in the sidebar for all users. Admin-only modules (like HR Documents) only show for super_admin / company_admin regardless.</p>
          </div>
          <Button onClick={save} disabled={saving} className="lift" data-testid="settings-flags-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {MODULES.map((m) => (
            <div key={m.k} className="flex items-center justify-between p-2.5 rounded-md bg-secondary/40 border border-border" data-testid={`flag-${m.k}`}>
              <div className="min-w-0">
                <div className="font-semibold text-sm">{m.l}</div>
                <div className="text-[11px] text-muted-foreground">{m.hint}</div>
              </div>
              <Switch checked={flags[m.k] !== false} onCheckedChange={(v) => set(m.k, v)} data-testid={`flag-toggle-${m.k}`} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EmailSettingsCard() {
  const [cfg, setCfg] = useState({ provider: "none", smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", smtp_encryption: "tls", resend_api_key: "", from_email: "", from_name: "", reply_to: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/email/config").then((r) => { setCfg({ ...cfg, ...r.data }); setLoading(false); }).catch(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      for (const k of Object.keys(cfg)) {
        if (cfg[k] !== "••••••••") patch[k] = cfg[k];
      }
      const r = await api.put("/email/config", patch);
      setCfg({ ...cfg, ...r.data });
      toast.success("Email config saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const testConn = async () => {
    setTesting(true); setResult(null);
    try { const r = await api.post("/email/test-connection"); setResult(r.data); }
    catch (e) { setResult({ ok: false, detail: e?.response?.data?.detail || "Failed" }); }
    finally { setTesting(false); }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error("Enter a recipient email");
    setSending(true); setResult(null);
    try { const r = await api.post("/email/test-send", { to_email: testEmail }); setResult(r.data); }
    catch (e) { setResult({ ok: false, detail: e?.response?.data?.detail || "Failed" }); }
    finally { setSending(false); }
  };

  if (loading) return null;

  return (
    <Card className="border-border" data-testid="settings-email-card">
      <CardContent className="p-5">
        <div className="display font-bold mb-1 flex items-center gap-2"><MailIcon className="size-4" /> Email / SMTP</div>
        <div className="text-xs text-muted-foreground mb-4">Configure your workspace email provider. Used for "Send candidate profile" in the editor. Supports Resend (API) and raw SMTP (Gmail, Zoho, Office365, SendGrid, etc.).</div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Provider">
            <Select value={cfg.provider || "none"} onValueChange={(v) => setCfg({ ...cfg, provider: v })}>
              <SelectTrigger data-testid="email-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not configured</SelectItem>
                <SelectItem value="resend">Resend (API)</SelectItem>
                <SelectItem value="raw_smtp">Raw SMTP</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="From email"><Input value={cfg.from_email || ""} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} placeholder="recruiter@youragency.com" data-testid="email-from" /></Field>
          <Field label="From name"><Input value={cfg.from_name || ""} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} placeholder="Your Agency Talent" /></Field>
          <Field label="Reply-to email"><Input value={cfg.reply_to || ""} onChange={(e) => setCfg({ ...cfg, reply_to: e.target.value })} placeholder="replies@youragency.com" /></Field>
        </div>

        {cfg.provider === "resend" && (
          <div className="mt-3">
            <Field label="Resend API key (get from resend.com/api-keys)"><Input type="password" value={cfg.resend_api_key || ""} onChange={(e) => setCfg({ ...cfg, resend_api_key: e.target.value })} data-testid="email-resend-key" /></Field>
          </div>
        )}

        {cfg.provider === "raw_smtp" && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="SMTP host"><Input value={cfg.smtp_host || ""} onChange={(e) => setCfg({ ...cfg, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="email-smtp-host" /></Field>
            <Field label="SMTP port"><Input type="number" value={cfg.smtp_port || 587} onChange={(e) => setCfg({ ...cfg, smtp_port: Number(e.target.value) })} data-testid="email-smtp-port" /></Field>
            <Field label="Encryption">
              <Select value={cfg.smtp_encryption || "tls"} onValueChange={(v) => setCfg({ ...cfg, smtp_encryption: v })}>
                <SelectTrigger data-testid="email-encryption"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tls">STARTTLS (port 587)</SelectItem>
                  <SelectItem value="ssl">SSL (port 465)</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="SMTP username"><Input value={cfg.smtp_user || ""} onChange={(e) => setCfg({ ...cfg, smtp_user: e.target.value })} data-testid="email-smtp-user" /></Field>
            <Field label="SMTP password (or Gmail app password)"><Input type="password" value={cfg.smtp_password || ""} onChange={(e) => setCfg({ ...cfg, smtp_password: e.target.value })} data-testid="email-smtp-pass" /></Field>
          </div>
        )}

        <div className="flex gap-2 mt-5 flex-wrap">
          <Button onClick={save} disabled={saving} className="lift" data-testid="email-save-button">{saving ? "Saving…" : "Save config"}</Button>
          <Button variant="outline" onClick={testConn} disabled={testing || cfg.provider === "none"} className="gap-2 lift" data-testid="email-test-conn-button">
            {testing ? <Loader2 className="size-4 animate-spin" /> : <Bot className="size-4" />} Test connection
          </Button>
          <div className="flex items-center gap-2 ml-auto">
            <Input placeholder="Send test to…" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="w-[220px]" data-testid="email-test-to" />
            <Button variant="outline" onClick={sendTest} disabled={sending || cfg.provider === "none"} className="gap-2 lift" data-testid="email-test-send-button">
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Bot className="size-4" />} Send test
            </Button>
          </div>
        </div>

        {result && (
          <div className={`mt-4 rounded-md border p-3 text-sm ${result.ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-destructive/30 bg-destructive/5"}`}>
            {result.ok ? <div className="text-emerald-700 dark:text-emerald-300">✓ {result.detail}</div> : <div className="text-destructive">{result.detail}</div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs font-semibold">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
